import Toastify from "toastify-js";

const DURATION_MS = { success: 3500, error: 6000, warning: 6000 };
const PENDING_KEY = "owlishbean.pendingToast";

type ToastType = "success" | "error" | "warning";

/**
 * Toastify-js handles DOM construction, stacking, and dismiss timing; its bundled CSS
 * theme is deliberately not imported (see styles/components.css's .toastify rules) —
 * all visible styling is ours, using the app's own design tokens, so success/error read
 * as unambiguously distinct (icon + colour, not colour alone).
 */
function show(message: string, type: ToastType): void {
  Toastify({
    text: message,
    duration: DURATION_MS[type],
    gravity: "top",
    position: "right",
    close: true,
    escapeMarkup: true,
    className: `app-toast app-toast-${type}`,
  }).showToast();
}

function queue(message: string, type: ToastType): void {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ message, type }));
  } catch {
    // sessionStorage unavailable (private browsing etc.) — the toast is just skipped.
  }
}

export const toast = {
  success: (message: string): void => show(message, "success"),
  error: (message: string): void => show(message, "error"),
  warning: (message: string): void => show(message, "warning"),
  /**
   * For actions that redirect via `location.assign` (a real page reload, not SPA
   * routing) — a toast fired right before that would be destroyed with the old
   * document before it's ever visible. This persists it across the reload instead.
   */
  queueSuccess: (message: string): void => queue(message, "success"),
  queueError: (message: string): void => queue(message, "error"),
  queueWarning: (message: string): void => queue(message, "warning"),
};

/** Called once at app boot (main.ts) to show a toast queued by the previous page, if any. */
export function flushPendingToast(): void {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(PENDING_KEY);
    if (raw) sessionStorage.removeItem(PENDING_KEY);
  } catch {
    return;
  }
  if (!raw) return;

  try {
    const pending = JSON.parse(raw) as { message: string; type: ToastType };
    show(pending.message, pending.type);
  } catch {
    // Malformed stored value — ignore.
  }
}
